(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!reducedMotion && document.documentElement.dataset.toMotion !== 'false') {
    document.documentElement.classList.add('to-motion');
  }

  class TouchOrbitStickyAtc extends HTMLElement {
    connectedCallback() {
      this.stickyButton = this.querySelector('[data-to-sticky-submit]');
      this.productInfo = document.querySelector('product-info');
      if (!this.productInfo || !this.stickyButton) return;

      this.handleStickyClick = () => {
        const mainButton = this.productInfo.querySelector('.product-form__submit');
        if (mainButton && !mainButton.disabled) mainButton.click();
      };
      this.stickyButton.addEventListener('click', this.handleStickyClick);

      this.observer = new IntersectionObserver(([entry]) => {
        this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }, { threshold: 0 });

      const sync = () => {
        const mainButton = this.productInfo.querySelector('.product-form__submit');
        if (!mainButton) return;
        if (mainButton !== this.observedButton) {
          if (this.observedButton) this.observer.unobserve(this.observedButton);
          this.observedButton = mainButton;
          this.observer.observe(mainButton);
        }
        this.stickyButton.disabled = mainButton.disabled;
        const label = mainButton.querySelector('span')?.textContent?.trim();
        if (label) this.stickyButton.querySelector('span').textContent = label;
        const livePrice = document.querySelector('[id^="price-"] .price-item--regular, [id^="price-"] .price-item--sale');
        const targetPrice = this.querySelector('[data-to-sticky-price]');
        if (livePrice && targetPrice) targetPrice.textContent = livePrice.textContent.trim();
      };
      sync();
      this.mutationObserver = new MutationObserver(sync);
      this.mutationObserver.observe(this.productInfo, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled']
      });
    }

    disconnectedCallback() {
      this.stickyButton?.removeEventListener('click', this.handleStickyClick);
      this.observer?.disconnect();
      this.mutationObserver?.disconnect();
    }
  }

  if (!customElements.get('to-sticky-atc')) customElements.define('to-sticky-atc', TouchOrbitStickyAtc);

  class TouchOrbitReviews extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-to-review-dialog]');
      this.photoDialog = this.querySelector('[data-to-review-photo-dialog]');
      this.ratingStep = this.querySelector('[data-to-review-step="rating"]');
      this.contentStep = this.querySelector('[data-to-review-step="content"]');

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-to-review-open]')) {
          if (!this.dialog) return;
          this.showStep('rating');
          if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
          else this.dialog.setAttribute('open', '');
          this.querySelector('.to-review-rating input')?.focus();
        }
        if (event.target.closest('[data-to-review-close]') || event.target === this.dialog) {
          this.closeDialog();
        }
        if (event.target.closest('[data-to-review-next]')) {
          const rating = this.querySelector('.to-review-rating input:checked');
          if (rating) this.showStep('content');
        }
        if (event.target.closest('[data-to-review-back]')) this.showStep('rating');
        const photoButton = event.target.closest('[data-to-review-photo]');
        if (photoButton) this.openPhoto(photoButton);
        if (event.target.closest('[data-to-review-photo-close]') || event.target === this.photoDialog) this.closePhoto();
      });

      this.addEventListener('change', (event) => {
        if (event.target.matches('.to-review-rating input')) {
          const rating = Number(event.target.value);
          const labels = [...this.querySelectorAll('.to-review-rating label')];
          labels.forEach((label, index) => label.classList.toggle('is-selected', index < rating));
          const next = this.querySelector('[data-to-review-next]');
          if (next) next.disabled = false;
          const preview = this.querySelector('[data-to-review-rating-preview]');
          if (preview) preview.textContent = '★'.repeat(rating) + '☆'.repeat(5 - rating);
        }
        if (event.target.matches('[data-to-review-filter]')) this.filterReviews(event.target.value);
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (this.photoDialog?.open) {
          event.preventDefault();
          this.closePhoto();
        } else if (this.dialog?.open) {
          event.preventDefault();
          this.closeDialog();
        }
      });

      this.dialog?.addEventListener('close', () => this.querySelector('[data-to-review-open]')?.focus());
      if (this.querySelector('[data-to-review-success]') && this.dialog) {
        if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
        else this.dialog.setAttribute('open', '');
      }
    }

    showStep(step) {
      if (!this.ratingStep || !this.contentStep) return;
      this.ratingStep.hidden = step !== 'rating';
      this.contentStep.hidden = step !== 'content';
      const target = step === 'rating' ? this.ratingStep : this.contentStep;
      target.querySelector('input, textarea, button')?.focus();
    }

    closeDialog() {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function' && this.dialog.open) this.dialog.close();
      else this.dialog.removeAttribute('open');
      this.querySelector('[data-to-review-open]')?.focus();
    }

    filterReviews(filter) {
      this.querySelectorAll('[data-to-review-card]').forEach((card) => {
        const visible = filter === 'all' || (filter === 'photos' && card.dataset.photo === 'true') || card.dataset.rating === filter;
        card.hidden = !visible;
      });
    }

    openPhoto(button) {
      if (!this.photoDialog) return;
      const image = this.photoDialog.querySelector('[data-to-review-photo-image]');
      if (!image) return;
      image.src = button.dataset.photoSrc || '';
      image.alt = button.dataset.photoAlt || '';
      if (typeof this.photoDialog.showModal === 'function') this.photoDialog.showModal();
      else this.photoDialog.setAttribute('open', '');
      this.photoDialog.querySelector('[data-to-review-photo-close]')?.focus();
    }

    closePhoto() {
      if (!this.photoDialog) return;
      if (typeof this.photoDialog.close === 'function' && this.photoDialog.open) this.photoDialog.close();
      else this.photoDialog.removeAttribute('open');
    }
  }

  if (!customElements.get('to-reviews')) customElements.define('to-reviews', TouchOrbitReviews);

  class TouchOrbitQuantityCards extends HTMLElement {
    connectedCallback() {
      this.handleClick = (event) => {
        const option = event.target.closest('[data-to-bundle-quantity]');
        if (!option || option.disabled) return;
        const input = this.getInput();
        if (!input) return;
        input.value = option.dataset.toBundleQuantity;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        this.sync();
      };
      this.addEventListener('click', this.handleClick);
      this.bindInput();
      this.productInfo = this.closest('product-info');
      if (this.productInfo) {
        this.quantityObserver = new MutationObserver(() => this.bindInput());
        this.quantityObserver.observe(this.productInfo, { childList: true, subtree: true });
      }
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => requestAnimationFrame(() => this.bindInput()));
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.input?.removeEventListener('change', this.handleInputChange);
      this.quantityObserver?.disconnect();
      this.variantUnsubscriber?.();
    }

    getInput() {
      return document.getElementById(this.dataset.quantityTarget);
    }

    bindInput() {
      const nextInput = this.getInput();
      if (nextInput !== this.input) {
        this.input?.removeEventListener('change', this.handleInputChange);
        this.input = nextInput;
        this.handleInputChange = () => this.sync();
        this.input?.addEventListener('change', this.handleInputChange);
      }
      this.sync();
    }

    sync() {
      if (!this.input) return;
      const min = Number(this.input.min || 1);
      const step = Number(this.input.step || 1);
      const max = this.input.max === '' ? Infinity : Number(this.input.max);
      const current = Number(this.input.value);
      let validCount = 0;
      this.querySelectorAll('[data-to-bundle-quantity]').forEach((button) => {
        const quantity = Number(button.dataset.toBundleQuantity);
        const valid = quantity >= min && quantity <= max && (quantity - min) % step === 0;
        button.disabled = !valid;
        button.setAttribute('aria-disabled', String(!valid));
        button.setAttribute('aria-pressed', String(valid && quantity === current));
        if (valid) validCount += 1;
      });
      const nativeQuantity = this.input.closest('.product-form__quantity');
      this.hidden = validCount === 0;
      nativeQuantity?.classList.toggle('to-native-quantity--hidden', validCount > 0);
    }
  }

  if (!customElements.get('to-quantity-cards')) customElements.define('to-quantity-cards', TouchOrbitQuantityCards);
})();
